import type { ReactNode } from 'react';
import { firstPendingField, legsChain, legsValid } from './leg_state.js';
import type { FlightPlannerCopy } from './planner_copy.js';
import type { PlannerTripType } from './planner_models.js';
import { SwapButton, TripTypeTabs as SharedTripTypeTabs } from '../../shared/route_controls.js';
import { TravelersField } from './travelers_field.js';
import type { PlannerGuide } from './guided_fill.js';
import { RouteField } from './route_field.js';
import { MultiLegBuilder } from './multi_leg_builder.js';
import { ArrowRightIcon, ArrowsLeftRightIcon, GlobeIcon } from '@phosphor-icons/react';
import { PlannerLegsField } from './planner_collapsed.js';
import { recentSection, type PlannerFormProps } from './planner_form_model.js';
import type { PlannerState } from './planner_state.js';
import type { BinaryPlanner } from './binary_planner.js';
import type { MultiPlanner } from './multi_planner.js';

/*
 * Os campos do planner fora das datas: as abas do tipo de viagem, a fileira
 * da rota, o QUEM, o construtor de trechos e a linha de trechos da faixa.
 * Saíram de `planner_form.tsx` pelo teto de linhas dele.
 */

/** As abas do tipo de viagem (Ida e volta / Somente ida / Várias cidades) — as
 *  `TripTypeTabs` do intelligence-ui-kit, as mesmas da busca de ônibus. Refletem
 *  `ui.tripType` e trocam o modo renderizado: `multi_leg` faz o card renderizar
 *  o construtor de trechos no lugar do par único. São o `Tabs` do DS na
 *  variante `pill` (#2362): a troca muda o FORMULÁRIO, e o `Tabs` do Base UI
 *  entrega `role="tab"`/`aria-selected`, que os hosts, os testes e os robôs do
 *  e2e já leem. */
export function TripTypeTabs({
  active,
  disabled,
  onSelect,
  copy,
}: Readonly<{
  active: PlannerTripType;
  disabled: boolean;
  onSelect: (type: PlannerTripType) => void;
  copy: FlightPlannerCopy;
}>) {
  return (
    <SharedTripTypeTabs
      tabs={[
        { key: 'round_trip', label: copy.tripTypeRoundTrip, Icon: ArrowsLeftRightIcon },
        { key: 'one_way', label: copy.tripTypeOneWay, Icon: ArrowRightIcon },
        { key: 'multi_leg', label: copy.tripTypeMultiLeg, Icon: GlobeIcon },
      ]}
      active={active}
      disabled={disabled}
      onSelect={onSelect}
    />
  );
}

export function RouteRowView({
  state,
  binary,
  whenField,
  band,
  readOnly,
  searchAirports,
  originSections,
  destinationSections,
  ctaLabel,
  submitDates,
  cta,
  sheet,
  guide,
  copy,
  airportLabel,
}: {
  readonly state: PlannerState;
  readonly binary: BinaryPlanner;
  readonly whenField: ReactNode;
  readonly band: boolean;
  readonly readOnly: boolean;
  /** Palco estreito do layout aberto: ORIGEM/DESTINO abrem em folha (#2653). */
  readonly sheet: boolean;
  /** O anel e a abertura por continuação de cada ponta (`useGuidedFill`). */
  readonly guide: PlannerGuide;
  readonly searchAirports: NonNullable<PlannerFormProps['searchAirports']>;
  readonly originSections: ReturnType<typeof recentSection>;
  readonly destinationSections: ReturnType<typeof recentSection>;
  readonly ctaLabel: string;
  readonly submitDates: () => void;
  /** O CTA inline dos DOIS modificadores do layout aberto — NAKED (#2167) e o
   *  CHAT sem saia (#2207, `inlineSearch`) —, já embrulhado na coluna
   *  `.fpl-field-cta`: fecha a fileira depois do QUANDO, como o CTA da faixa.
   *  Não-nulo nos dois; o que muda entre eles é só o rótulo (pílula "Buscar"
   *  vs. lupa only-icon), decidido em `inlineCtas`. `null` fora dos dois
   *  (obrigatória e não `?:` pra viajar sem spread condicional — o form está
   *  no teto de complexidade). O da faixa segue construído aqui dentro (são
   *  estilos e rótulos distintos). */
  readonly cta: ReactNode;
  readonly copy: FlightPlannerCopy;
  readonly airportLabel: PlannerFormProps['airportLabel'];
}) {
  return (
    /* Sem o QUEM aqui (#2362): ele mora isolado na linha de cima nos DOIS modos
       (`whoRow`) — a fileira é só a rota, o quando e o CTA. */
    <div className="fpl-filter">
      <RouteField
        label={copy.from}
        endpoint="origin"
        sheet={sheet}
        value={state.origin}
        selectedLabel={state.originLabel}
        disabled={readOnly}
        placeholder={copy.fromEmpty}
        promptText={copy.airportSearchPrompt}
        emptyText={copy.noAirports}
        onSearch={searchAirports}
        onChange={binary.pickOrigin}
        copy={copy}
        airportLabel={airportLabel}
        {...(originSections ? { sections: originSections } : {})}
        {...guide.place(null, 'origin')}
      />
      <SwapButton label={copy.swap} disabled={readOnly} onSwap={binary.swap} />
      <RouteField
        label={copy.to}
        endpoint="destination"
        sheet={sheet}
        value={state.destination}
        selectedLabel={state.destinationLabel}
        disabled={readOnly}
        placeholder={copy.toEmpty}
        promptText={copy.airportSearchPrompt}
        emptyText={copy.noAirports}
        onSearch={searchAirports}
        onChange={binary.pickDestination}
        copy={copy}
        airportLabel={airportLabel}
        {...(destinationSections ? { sections: destinationSections } : {})}
        {...guide.place(null, 'destination')}
      />
      {binary.sameEndpoints && (
        <p className="fpl-route-error" role="alert">
          {copy.sameAirports}
        </p>
      )}
      {whenField}
      {cta}
      {band && !readOnly && (
        <div className="fpl-field fpl-field-cta">
          <button
            type="button"
            className="fui-btn fui-btn-primary fpl-cta"
            disabled={!binary.ready}
            onClick={submitDates}
          >
            {ctaLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/** O campo QUEM com o rótulo por fora — a MESMA coluna nas duas casas dele: a
 *  fileira binária e o cabeçalho do multi (#2136: UM Quem só, acima das linhas,
 *  na primeira posição do ida e volta — o valor é da VIAGEM). */
export function WhoField({
  state,
  initial,
  readOnly,
  sheet,
  directory,
  copy,
}: {
  readonly state: PlannerState;
  readonly initial: PlannerFormProps['initial'];
  readonly readOnly: boolean;
  /** A lista abre em FOLHA de tela cheia (#2653) — o palco estreito. */
  readonly sheet: boolean;
  /** Ver `PlannerFormProps.travelerDirectory`. */
  readonly directory: PlannerFormProps['travelerDirectory'];
  readonly copy: FlightPlannerCopy;
}) {
  return (
    <div className="genui2 fpl-field fpl-field-who bg-transparent">
      <span className="fpl-flabel">{copy.who}</span>
      <TravelersField
        value={state.travelers}
        policyTravelerId={state.policyTravelerId}
        requesterId={initial.requesterId}
        readOnly={readOnly}
        {...(sheet ? { sheet: { title: copy.who, backLabel: copy.back } } : {})}
        directory={directory}
        copy={copy.travelers}
        onChange={({ travelers, policyTravelerId }) => {
          state.setTravelers(travelers);
          state.setPolicyTravelerId(policyTravelerId);
        }}
      />
    </div>
  );
}

export function LegBuilderView({
  state,
  multi,
  band,
  collapsed,
  readOnly,
  searchAirports,
  originSections,
  destinationSections,
  cta,
  legDateField,
  placeSheet,
  stacked,
  guide,
  copy,
  locale,
  airportLabel,
}: {
  readonly state: PlannerState;
  readonly multi: MultiPlanner;
  readonly band: boolean;
  readonly collapsed: boolean;
  readonly readOnly: boolean;
  readonly searchAirports: NonNullable<PlannerFormProps['searchAirports']>;
  readonly originSections: ReturnType<typeof recentSection>;
  readonly destinationSections: ReturnType<typeof recentSection>;
  readonly cta?: ReactNode;
  /** Ver `MultiLegBuilder.guide`. */
  readonly guide: PlannerGuide;
  /** O QUANDO por linha como popover (#2136) — só o layout aberto passa; o
   *  editor da faixa/colapsado segue com o espelho + grade inline. */
  readonly legDateField?: (index: number) => ReactNode;
  /** DE/PARA das linhas em FOLHA (#2653) — ver `MultiLegBuilder.placeSheet`. */
  readonly placeSheet: boolean;
  /** Linhas em COLUNA, todas rotuladas — ver `MultiLegBuilder.stacked`. */
  readonly stacked: boolean;
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
  readonly airportLabel: PlannerFormProps['airportLabel'];
}) {
  return (
    <MultiLegBuilder
      copy={copy}
      locale={locale}
      airportLabel={airportLabel}
      placeSheet={placeSheet}
      stacked={stacked}
      {...(cta ? { cta } : {})}
      legs={state.legs}
      legIds={state.legIds}
      editingLegId={state.editingLegId}
      legFocus={state.legFocus}
      queuedLegId={multi.queuedLegId}
      onLeg={multi.changeLeg}
      onEdit={(id) => {
        // Abrir outra perna é gesto explícito — marca ANTES de mudar o
        // estado que alimenta o effect único do heat por perna.
        multi.noteHeatGestureUser();
        // Trocar a perna em edição fecha o popover de data que estivesse aberto
        // em outra: o calendário é sempre o da perna em edição.
        if (id !== state.editingLegId) state.setDateOpenLegId(null);
        state.setEditingLegId(id);
        const leg = state.legs[state.legIds.indexOf(id)];
        state.setLegFocus(leg ? (firstPendingField(leg) ?? 'date') : null);
      }}
      onAdd={multi.addLeg}
      onRemove={multi.removeLeg}
      searchAirports={searchAirports}
      suggestions={{
        ...(originSections ? { origin: originSections } : {}),
        ...(destinationSections ? { destination: destinationSections } : {}),
      }}
      readOnly={readOnly}
      // Linhas sempre ABERTAS no layout aberto (#2136); a faixa/colapsado e o
      // card congelado preservam o colapso (ver a doc da prop no builder).
      alwaysOpen={!band && !collapsed && !readOnly}
      {...(legDateField ? { dateField: legDateField } : {})}
      glowLegId={multi.glowLegId}
      guide={guide}
    />
  );
}

export function LegsFieldView({
  state,
  range,
  note,
  readOnly,
  band,
  cta,
  builder,
  calendar,
  openLegs,
  restoreLegs,
  submitMulti,
  copy,
}: {
  readonly state: PlannerState;
  readonly range?: string;
  readonly note?: string;
  readonly readOnly: boolean;
  readonly band: boolean;
  readonly cta: ReactNode;
  readonly builder: ReactNode;
  readonly calendar: ReactNode;
  readonly openLegs: () => void;
  readonly restoreLegs: () => void;
  readonly submitMulti: () => void;
  readonly copy: FlightPlannerCopy;
}) {
  return (
    <PlannerLegsField
      chain={legsChain(state.legs).map((place) => place || copy.multiLegSummaryUndefined)}
      {...(range ? { dates: range } : {})}
      editLabel={copy.multiLegEditLegs}
      panelLabel={copy.tripTypeMultiLeg}
      cancelLabel={copy.cancelEdit}
      updateLabel={copy.applyEdit}
      updateDisabled={!legsValid(state.legs)}
      disabled={readOnly}
      {...(note ? { note } : {})}
      {...(band ? { cta } : {})}
      onOpen={openLegs}
      onDismiss={restoreLegs}
      onUpdate={band ? () => {} : submitMulti}
    >
      {builder}
      {calendar}
    </PlannerLegsField>
  );
}
